import React, { useState, useEffect } from "react";
import { View, Text, ActivityIndicator } from "react-native";
import { styles } from "../../style/FoundryStyle.js";
import { SCREEN_IDS } from "../FullComponent.js";
import { fullDataFundicionPiece } from "../../services/ConnectDataBase.js";
import { typeConnect } from "../../constants/Constants.js";
import { translateText } from "../../translations/translationService";
import { useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Typography,
  Button,
  Skeleton,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import * as XLSX from 'xlsx';
import { BRAND_COLORS, BACKGROUND_COLORS, TEXT_COLORS, FUNDICION_STATUS_COLORS } from "../../style/colors.js";
import { convertFundicionToFine, getPurityFactor } from "../../constants/MetalPurityFactors.js";
import { notify } from "../../services/ui/notify";

// Configuración de skeletons de carga
const SKELETON_CONFIG = {
  rows: 5,              // Número de filas de skeletons
  height: 20,           // Altura de los skeletons en px
  widthLabel: "100%",   // Ancho del skeleton de etiqueta
  widthValue: "100%",   // Ancho del skeleton de valor
};

// Constantes para la configuración de la tabla
const TABLE_CONFIG = {
  COLUMNS: [
    { key: "retraso", label: "En retraso" },
    { key: "uno", label: "1" },
    { key: "cinco", label: "2 - 5" },
    { key: "quince", label: "6 - 15" },
    { key: "treinta", label: "16 - 30" },
    { key: "sesenta", label: "31 - 60" },
    { key: "faltanDosMeses", label: "+ 2 Meses" }
  ],
  CELL_STYLES: {
    padding: "1px 2px",
    headerPadding: "2px 4px",
    subHeaderPadding: "1px 2px"
  },
  FONT_SIZES: {
    normal: "0.75rem",
    small: "0.75rem",
    header: "0.8rem"
  }
};

// Configuración para la tabla unificada (cantidad + aleación + fino)
const UNIFIED_TABLE_CONFIG = {
  COLUMNS: [
    { key: "retraso", label: "En retraso", subColumns: ["cantidad", "aleacion", "fino"] },
    { key: "uno", label: "1", subColumns: ["cantidad", "aleacion", "fino"] },
    { key: "cinco", label: "2 - 5", subColumns: ["cantidad", "aleacion", "fino"] },
    { key: "quince", label: "6 - 15", subColumns: ["cantidad", "aleacion", "fino"] },
    { key: "treinta", label: "16 - 30", subColumns: ["cantidad", "aleacion", "fino"] },
    { key: "sesenta", label: "31 - 60", subColumns: ["cantidad", "aleacion", "fino"] },
    { key: "faltanDosMeses", label: "+ 2 Meses", subColumns: ["cantidad", "aleacion", "fino"] }
  ]
};

// Configuración de rangos de días
const DAY_RANGES = {
  RETRASO: { min: -Infinity, max: 0 },
  UNO: { min: 1, max: 1 },
  CINCO: { min: 2, max: 5 },
  QUINCE: { min: 6, max: 15 },
  TREINTA: { min: 16, max: 30 },
  SESENTA: { min: 31, max: 60 },
  MAS_DOS_MESES: { min: 61, max: Infinity }
};

// Funciones auxiliares
const getDayRange = (diffDays) => {
  if (diffDays <= DAY_RANGES.RETRASO.max) return "retraso";
  if (diffDays >= DAY_RANGES.UNO.min && diffDays <= DAY_RANGES.UNO.max) return "uno";
  if (diffDays >= DAY_RANGES.CINCO.min && diffDays <= DAY_RANGES.CINCO.max) return "cinco";
  if (diffDays >= DAY_RANGES.QUINCE.min && diffDays <= DAY_RANGES.QUINCE.max) return "quince";
  if (diffDays >= DAY_RANGES.TREINTA.min && diffDays <= DAY_RANGES.TREINTA.max) return "treinta";
  if (diffDays >= DAY_RANGES.SESENTA.min && diffDays <= DAY_RANGES.SESENTA.max) return "sesenta";
  if (diffDays >= DAY_RANGES.MAS_DOS_MESES.min) return "faltanDosMeses";
  return "retraso";
};

const createEmptyDataStructure = () => ({
  total: 0,
  retraso: 0,
  uno: 0,
  cinco: 0,
  quince: 0,
  treinta: 0,
  sesenta: 0,
  faltanDosMeses: 0,
});

const FundicionStatus = ({ navigation }) => {
  const currentScreenId = SCREEN_IDS.PIECES_FUNDICION;
  const [datosFundicion, setDatosFundicion] = useState([]);
  const [datosOroCordoba, setDatosOroCordoba] = useState([]);
  const [datosOroCordobaFino, setDatosOroCordobaFino] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalsumaValores, setTotalsumaValores] = useState({});
  const [totalOroCordoba, setTotalOroCordoba] = useState({});
  const [totalOroCordobaFino, setTotalOroCordobaFino] = useState({});
  const lang = useSelector((state) => state.lang.value);

  useEffect(() => {
    const processData = async () => {
      try {
        setIsLoading(true);
        const dataOk = await fullDataFundicionPiece(
          currentScreenId,
          typeConnect[0]
        );
        if (dataOk) {
          setIsLoading(false);
          const groupedData = calculateData(dataOk, 'cantidad');
          const oroCordobaData = calculateData(dataOk, 'gramos');
          
          // Convertir los datos de oro Córdoba a pesos finos
          const oroCordobaDataFino = convertFundicionToFine(Object.values(oroCordobaData));
          
          setDatosFundicion(Object.values(groupedData));
          setDatosOroCordoba(Object.values(oroCordobaData));
          setDatosOroCordobaFino(oroCordobaDataFino);
          setTotalsumaValores(sumarValores(Object.values(groupedData)));
          setTotalOroCordoba(sumarValores(Object.values(oroCordobaData)));
          setTotalOroCordobaFino(sumarValores(oroCordobaDataFino));
        }
      } catch (error) {
        console.error(error);
      }
    };

    processData();
  }, []);

  function sumarValores(array) {
    return array.reduce((resultado, elemento) => {
      for (const key in elemento) {
        if (key !== "aleacion") {
          if (resultado[key]) {
            resultado[key] += elemento[key];
          } else {
            resultado[key] = elemento[key];
          }
        }
      }
      return resultado;
    }, {});
  }

  const calculateData = (data, valueField = 'cantidad') => {
    const today = new Date();
    return data.reduce((acc, item) => {
      const aleacion = item.aleacion || "Desconocido";
      const entrega = new Date(item.fechaentrega.date);
      const diffTime = Math.abs(entrega.getTime() - today.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 3600 * 24));
      
      let value = valueField === 'cantidad' 
        ? Math.round(parseFloat(item[valueField]))
        : parseFloat(item[valueField]) || 0;
      
      value = isNaN(value) ? 0 : value;

      if (!acc[aleacion]) {
        acc[aleacion] = createEmptyDataStructure();
      }
      
      acc[aleacion].total += value;
      acc[aleacion].aleacion = item.descripcion;
      
      const range = getDayRange(diffDays);
      acc[aleacion][range] += value;

      return acc;
    }, {});
  };


  const getCellStyle = (value, column) => {
    let backgroundColor, color, fontWeight;
    if (value > 0) {
      switch (column) {
        case "retraso":
          backgroundColor = BRAND_COLORS.PRIMARY;
          color = TEXT_COLORS.LIGHT;
          fontWeight = "normal";
          break;
        case "uno":
          backgroundColor = FUNDICION_STATUS_COLORS.STATUS_VERY_HIGH;
          color = TEXT_COLORS.LIGHT;
          fontWeight = "normal";
          break;
        case "cinco":
          backgroundColor = FUNDICION_STATUS_COLORS.STATUS_HIGH;
          color = TEXT_COLORS.DARK;
          fontWeight = "normal";
          break;
        case "quince":
          backgroundColor = FUNDICION_STATUS_COLORS.STATUS_MEDIUM_HIGH;
          color = TEXT_COLORS.DARK;
          fontWeight = "normal";
          break;
        case "treinta":
          backgroundColor = FUNDICION_STATUS_COLORS.STATUS_MEDIUM;
          color = TEXT_COLORS.DARK;
          fontWeight = "normal";
          break;
        case "sesenta":
          backgroundColor = FUNDICION_STATUS_COLORS.STATUS_LOW;
          color = TEXT_COLORS.DARK;
          fontWeight = "normal";
          break;
        case "faltanDosMeses":
          backgroundColor = FUNDICION_STATUS_COLORS.STATUS_VERY_LOW;
          color = TEXT_COLORS.DARK;
          fontWeight = "normal";
          break;
        default:
          backgroundColor = "transparent";
          color = TEXT_COLORS.DARK;
          fontWeight = "normal";
      }
    } else {
      backgroundColor = "transparent";
      color = TEXT_COLORS.DARK;
      fontWeight = "normal";
    }
    return { backgroundColor, color, fontWeight };
  };

  const formatGramos = (value) => {
    if (value === 0 || !value) return "-";
    return value.toFixed(2);
  };

  // Función para formatear cantidades (enteros)
  const formatCantidad = (value) => {
    if (value === 0 || !value) return "-";
    return Math.round(parseFloat(value));
  };

  // Función para exportar a Excel
  const exportToExcel = () => {
    if (!datosFundicion.length || !datosOroCordoba.length || !datosOroCordobaFino.length) {
      notify.info("No hay datos para exportar");
      return;
    }

    // Crear los headers de la tabla
    const headers = [
      "Días",
      "En retraso - Cantidad", "En retraso - Aleación", "En retraso - Fino",
      "1 - Cantidad", "1 - Aleación", "1 - Fino",
      "2-5 - Cantidad", "2-5 - Aleación", "2-5 - Fino",
      "6-15 - Cantidad", "6-15 - Aleación", "6-15 - Fino",
      "16-30 - Cantidad", "16-30 - Aleación", "16-30 - Fino",
      "31-60 - Cantidad", "31-60 - Aleación", "31-60 - Fino",
      "+2 Meses - Cantidad", "+2 Meses - Aleación", "+2 Meses - Fino",
      "Total - Cantidad", "Total - Aleación", "Total - Fino"
    ];

    // Crear los datos de la tabla
    const excelData = [headers];

    // Añadir filas de datos
    datosFundicion.forEach((cantidadItem, index) => {
      const aleacionItem = datosOroCordoba[index];
      const finoItem = datosOroCordobaFino[index];
      
      if (aleacionItem && finoItem) {
        const row = [
          cantidadItem.aleacion || "-",
          formatCantidad(cantidadItem.retraso),
          formatGramos(aleacionItem.retraso),
          formatGramos(finoItem.retraso),
          formatCantidad(cantidadItem.uno),
          formatGramos(aleacionItem.uno),
          formatGramos(finoItem.uno),
          formatCantidad(cantidadItem.cinco),
          formatGramos(aleacionItem.cinco),
          formatGramos(finoItem.cinco),
          formatCantidad(cantidadItem.quince),
          formatGramos(aleacionItem.quince),
          formatGramos(finoItem.quince),
          formatCantidad(cantidadItem.treinta),
          formatGramos(aleacionItem.treinta),
          formatGramos(finoItem.treinta),
          formatCantidad(cantidadItem.sesenta),
          formatGramos(aleacionItem.sesenta),
          formatGramos(finoItem.sesenta),
          formatCantidad(cantidadItem.faltanDosMeses),
          formatGramos(aleacionItem.faltanDosMeses),
          formatGramos(finoItem.faltanDosMeses),
          formatCantidad(cantidadItem.total),
          formatGramos(aleacionItem.total),
          formatGramos(finoItem.total)
        ];
        excelData.push(row);
      }
    });

    // Añadir fila de totales
    const totalRow = [
      "TOTAL",
      formatCantidad(totalsumaValores.retraso),
      formatGramos(totalOroCordoba.retraso),
      formatGramos(totalOroCordobaFino.retraso),
      formatCantidad(totalsumaValores.uno),
      formatGramos(totalOroCordoba.uno),
      formatGramos(totalOroCordobaFino.uno),
      formatCantidad(totalsumaValores.cinco),
      formatGramos(totalOroCordoba.cinco),
      formatGramos(totalOroCordobaFino.cinco),
      formatCantidad(totalsumaValores.quince),
      formatGramos(totalOroCordoba.quince),
      formatGramos(totalOroCordobaFino.quince),
      formatCantidad(totalsumaValores.treinta),
      formatGramos(totalOroCordoba.treinta),
      formatGramos(totalOroCordobaFino.treinta),
      formatCantidad(totalsumaValores.sesenta),
      formatGramos(totalOroCordoba.sesenta),
      formatGramos(totalOroCordobaFino.sesenta),
      formatCantidad(totalsumaValores.faltanDosMeses),
      formatGramos(totalOroCordoba.faltanDosMeses),
      formatGramos(totalOroCordobaFino.faltanDosMeses),
      formatCantidad(totalsumaValores.total),
      formatGramos(totalOroCordoba.total),
      formatGramos(totalOroCordobaFino.total)
    ];
    excelData.push(totalRow);

    // Crear el workbook y worksheet
    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Estado Fundición");

    // Generar el archivo Excel
    const fileName = `Estado_Fundicion_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Componente para el encabezado de la tabla unificada
  const UnifiedTableHeader = ({ lang, onExport, isLoading, hasData }) => (
    <TableHead>
      {/* Primera fila de headers */}
      <TableRow>
        <TableCell
          rowSpan={1}
          style={{
            ...styles.headerCellRepairScreen,
            backgroundColor: FUNDICION_STATUS_COLORS.STATUS_RETRASO,
            padding: TABLE_CONFIG.CELL_STYLES.headerPadding,
            textAlign: "center",
            verticalAlign: "middle",
            color: "white"
          }}>
          <Text style={{ 
            fontWeight: "500", 
            fontSize: TABLE_CONFIG.FONT_SIZES.header,
            color: "white"
          }}>
            {translateText("Días", lang)}
          </Text>
        </TableCell>
        {UNIFIED_TABLE_CONFIG.COLUMNS.map((column) => (
          <TableCell
            key={column.key}
            colSpan={3}
            style={{
              ...styles.headerCellRepairScreen,
              backgroundColor: FUNDICION_STATUS_COLORS.STATUS_RETRASO,
              borderBottom: `1px solid rgba(255,255,255,0.1)`,
              padding: TABLE_CONFIG.CELL_STYLES.headerPadding,
              textAlign: "center",
              color: "white"
            }}>
            <Text style={{ 
              fontWeight: "500", 
              fontSize: TABLE_CONFIG.FONT_SIZES.header,
              color: "white"
            }}>
              {column.key === "retraso" ? translateText(column.label, lang) : column.label}
            </Text>
          </TableCell>
        ))}
        <TableCell
          colSpan={3}
          style={{
            ...styles.headerCellRepairScreen,
            backgroundColor: FUNDICION_STATUS_COLORS.STATUS_RETRASO,
            borderBottom: `1px solid rgba(255,255,255,0.1)`,
            padding: TABLE_CONFIG.CELL_STYLES.headerPadding,
            textAlign: "center",
            color: "white"
          }}>
          <Text style={{ 
            fontWeight: "500", 
            fontSize: TABLE_CONFIG.FONT_SIZES.header,
            color: "white"
          }}>
            {translateText("Total", lang)}
          </Text>
        </TableCell>
      </TableRow>
      {/* Segunda fila de headers */}
      <TableRow>
        {/* Celda para el botón de exportar (debajo de "Días") */}
        <TableCell
          style={{
            ...styles.headerCellRepairScreen,
            backgroundColor: FUNDICION_STATUS_COLORS.STATUS_RETRASO,
            borderBottom: `1px solid rgba(255,255,255,0.1)`,
            textAlign: "center",
            verticalAlign: "middle",
            padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
          }}>
          <Button
            startIcon={<Download style={{ fontSize: "12px" }} />}
            onClick={onExport}
            disabled={isLoading || !hasData}
            sx={{
              color: "white",
              fontSize: "0.7rem",
              padding: "1px 3px",
              minWidth: "auto",
              "& .MuiButton-startIcon": {
                marginRight: "2px",
                marginLeft: 0
              },
              "&:hover": {
                backgroundColor: BRAND_COLORS.PRIMARY_LIGHT,
              },
              "&:disabled": {
                backgroundColor: FUNDICION_STATUS_COLORS.BUTTON_DISABLED_BG,
                color: FUNDICION_STATUS_COLORS.BUTTON_DISABLED_TEXT
              }
            }}
          >
            Excel
          </Button>
        </TableCell>
        {UNIFIED_TABLE_CONFIG.COLUMNS.map((column) => (
          <React.Fragment key={column.key}>
            <TableCell
              style={{
                ...styles.headerCellRepairScreen,
                backgroundColor: "#f5f5f5",
                borderRight: `1px solid rgba(0,0,0,0.08)`,
                padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
                textAlign: "center",
              }}>
              <Text style={{ 
                fontSize: TABLE_CONFIG.FONT_SIZES.normal,
                fontWeight: "500",
                color: "#000000"
              }}>Cantidad</Text>
            </TableCell>
            <TableCell
              style={{
                ...styles.headerCellRepairScreen,
                backgroundColor: "#f5f5f5",
                borderRight: `1px solid rgba(0,0,0,0.08)`,
                padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
                textAlign: "center",
              }}>
              <Text style={{ 
                fontSize: TABLE_CONFIG.FONT_SIZES.normal,
                fontWeight: "500",
                color: "#000000"
              }}>Aleación</Text>
            </TableCell>
            <TableCell
              style={{
                ...styles.headerCellRepairScreen,
                backgroundColor: "#f5f5f5",
                borderRight: `1px solid rgba(0,0,0,0.08)`,
                padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
                textAlign: "center",
              }}>
              <Text style={{ 
                fontSize: TABLE_CONFIG.FONT_SIZES.normal,
                fontWeight: "500",
                color: "#000000"
              }}>Fino</Text>
            </TableCell>
          </React.Fragment>
        ))}
        {/* Subcolumnas para Total */}
    
        <TableCell
          style={{
            ...styles.headerCellRepairScreen,
            backgroundColor: "#f5f5f5",
            borderRight: `1px solid rgba(0,0,0,0.08)`,
            padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
            textAlign: "center",
          }}>
          <Text style={{ 
            fontSize: TABLE_CONFIG.FONT_SIZES.normal,
            fontWeight: "500",
            color: "#000000"
          }}>Cantidad</Text>
        </TableCell>
        <TableCell
          style={{
            ...styles.headerCellRepairScreen,
            backgroundColor: "#f5f5f5",
            borderRight: `1px solid rgba(0,0,0,0.08)`,
            padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
            textAlign: "center",
          }}>
          <Text style={{ 
            fontSize: TABLE_CONFIG.FONT_SIZES.normal,
            fontWeight: "500",
            color: "#000000"
          }}>Aleación</Text>
        </TableCell>
        <TableCell
          style={{
            ...styles.headerCellRepairScreen,
            backgroundColor: "#f5f5f5",
            padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
            textAlign: "center",
          }}>
          <Text style={{ 
            fontSize: TABLE_CONFIG.FONT_SIZES.normal,
            fontWeight: "500",
            color: "#000000"
          }}>Fino</Text>
        </TableCell>
      </TableRow>
    </TableHead>
  );


  // Componente para las filas de datos unificadas (cantidad + aleación + fino)
  const UnifiedDataRow = ({ cantidadItem, aleacionItem, finoItem, formatValue = (val) => val || "-", index }) => (
    <TableRow style={{
      ...styles.rowRepairScreen,
      backgroundColor: index % 2 === 0 ? FUNDICION_STATUS_COLORS.TABLE_ROW_EVEN : FUNDICION_STATUS_COLORS.TABLE_ROW_ODD,
      borderBottom: `1px solid ${FUNDICION_STATUS_COLORS.TABLE_BORDER_SECONDARY}`
    }}>
      <TableCell
        style={{
          ...styles.cellRepairScreen,
          padding: TABLE_CONFIG.CELL_STYLES.padding,
          borderBottom: `1px solid rgba(0,0,0,0.08)`,
        }}>
        <Text style={{ 
          fontSize: TABLE_CONFIG.FONT_SIZES.normal,
          fontWeight: "500",
          color: "#174045"
        }}>
          {cantidadItem.aleacion ? cantidadItem.aleacion : "-"}
        </Text>
      </TableCell>
      {UNIFIED_TABLE_CONFIG.COLUMNS.map((column) => (
        <React.Fragment key={column.key}>
          {/* Celda de cantidad */}
          <TableCell
            style={{
              ...styles.cellRepairScreen,
              padding: TABLE_CONFIG.CELL_STYLES.padding,
              textAlign: "center",
              backgroundColor: cantidadItem[column.key] > 0 
                ? getCellStyle(cantidadItem[column.key], column.key).backgroundColor 
                : (index % 2 === 0 ? "#ffffff" : "#f5f5f5"),
              borderRight: `1px solid rgba(0,0,0,0.08)`,
              borderBottom: `1px solid rgba(0,0,0,0.08)`,
            }}>
            <Text
              style={{
                color: cantidadItem[column.key] > 0 
                  ? getCellStyle(cantidadItem[column.key], column.key).color 
                  : "#000000",
                fontWeight: getCellStyle(cantidadItem[column.key], column.key).fontWeight,
                fontSize: TABLE_CONFIG.FONT_SIZES.normal,
              }}>
              {formatCantidad(cantidadItem[column.key])}
            </Text>
          </TableCell>
          {/* Celda de aleación */}
          <TableCell
            style={{
              ...styles.cellRepairScreen,
              padding: TABLE_CONFIG.CELL_STYLES.padding,
              textAlign: "center",
              backgroundColor: aleacionItem[column.key] > 0 
                ? getCellStyle(aleacionItem[column.key], column.key).backgroundColor 
                : (index % 2 === 0 ? "#ffffff" : "#f5f5f5"),
              borderRight: `1px solid rgba(0,0,0,0.08)`,
              borderBottom: `1px solid rgba(0,0,0,0.08)`,
            }}>
            <Text
              style={{
                color: aleacionItem[column.key] > 0 
                  ? getCellStyle(aleacionItem[column.key], column.key).color 
                  : "#000000",
                fontWeight: getCellStyle(aleacionItem[column.key], column.key).fontWeight,
                fontSize: TABLE_CONFIG.FONT_SIZES.small
              }}>
              {formatValue(aleacionItem[column.key])}
            </Text>
          </TableCell>
          {/* Celda de fino */}
          <TableCell
            style={{
              ...styles.cellRepairScreen,
              padding: TABLE_CONFIG.CELL_STYLES.padding,
              textAlign: "center",
              backgroundColor: finoItem[column.key] > 0 
                ? getCellStyle(finoItem[column.key], column.key).backgroundColor 
                : (index % 2 === 0 ? "#ffffff" : "#f5f5f5"),
              borderRight: `1px solid rgba(0,0,0,0.08)`,
              borderBottom: `1px solid rgba(0,0,0,0.08)`,
            }}>
            <Text
              style={{
                color: finoItem[column.key] > 0 
                  ? getCellStyle(finoItem[column.key], column.key).color 
                  : "#000000",
                fontWeight: getCellStyle(finoItem[column.key], column.key).fontWeight,
                fontSize: TABLE_CONFIG.FONT_SIZES.small
              }}>
              {formatValue(finoItem[column.key])}
            </Text>
          </TableCell>
        </React.Fragment>
      ))}
      {/* Celda de total cantidad */}
      <TableCell
        style={{
          ...styles.cellRepairScreen,
          padding: TABLE_CONFIG.CELL_STYLES.padding,
          textAlign: "center",
          backgroundColor: cantidadItem.total > 0 
            ? getCellStyle(cantidadItem.total, "total").backgroundColor 
            : (index % 2 === 0 ? "#ffffff" : "#f5f5f5"),
          borderRight: `1px solid rgba(0,0,0,0.08)`,
          borderBottom: `1px solid rgba(0,0,0,0.08)`,
        }}>
        <Text
          style={{
            color: cantidadItem.total > 0 
              ? getCellStyle(cantidadItem.total, "total").color 
              : "#000000",
            fontWeight: getCellStyle(cantidadItem.total, "total").fontWeight,
            fontSize: TABLE_CONFIG.FONT_SIZES.normal,
          }}>
          {formatCantidad(cantidadItem.total)}
        </Text>
      </TableCell>
      {/* Celda de total aleación */}
      <TableCell
        style={{
          ...styles.cellRepairScreen,
          padding: TABLE_CONFIG.CELL_STYLES.padding,
          textAlign: "center",
          backgroundColor: aleacionItem.total > 0 
            ? getCellStyle(aleacionItem.total, "total").backgroundColor 
            : (index % 2 === 0 ? "#ffffff" : "#f5f5f5"),
          borderRight: `1px solid rgba(0,0,0,0.08)`,
          borderBottom: `1px solid rgba(0,0,0,0.08)`,
        }}>
        <Text
          style={{
            color: aleacionItem.total > 0 
              ? getCellStyle(aleacionItem.total, "total").color 
              : "#000000",
            fontWeight: getCellStyle(aleacionItem.total, "total").fontWeight,
            fontSize: TABLE_CONFIG.FONT_SIZES.normal
          }}>
          {formatValue(aleacionItem.total)}
        </Text>
      </TableCell>
      {/* Celda de total fino */}
      <TableCell
        style={{
          ...styles.cellRepairScreen,
          padding: TABLE_CONFIG.CELL_STYLES.padding,
          textAlign: "center",
          backgroundColor: finoItem.total > 0 
            ? getCellStyle(finoItem.total, "total").backgroundColor 
            : (index % 2 === 0 ? "#ffffff" : "#f5f5f5"),
          borderBottom: `1px solid rgba(0,0,0,0.08)`,
        }}>
        <Text
          style={{
            color: finoItem.total > 0 
              ? getCellStyle(finoItem.total, "total").color 
              : "#000000",
            fontWeight: getCellStyle(finoItem.total, "total").fontWeight,
            fontSize: TABLE_CONFIG.FONT_SIZES.normal
          }}>
          {formatValue(finoItem.total)}
        </Text>
      </TableCell>
    </TableRow>
  );


  // Componente para la fila de totales unificada
  const UnifiedTotalRow = ({ cantidadTotals, aleacionTotals, finoTotals, formatValue = (val) => val || "-" }) => (
    <TableRow style={{
      backgroundColor: FUNDICION_STATUS_COLORS.TABLE_TOTAL_ROW,
      borderTop: `2px solid ${FUNDICION_STATUS_COLORS.TABLE_TOTAL_BORDER}`
    }}>
      <TableCell
        style={{ 
          ...styles.cellRepairScreen, 
          padding: TABLE_CONFIG.CELL_STYLES.padding,
          borderBottom: `1px solid rgba(0,0,0,0.15)`,
        }}>
        <Text style={{ 
          fontSize: TABLE_CONFIG.FONT_SIZES.normal, 
          fontWeight: "600",
          color: "#174045"
        }}>
          Total
        </Text>
      </TableCell>
      {UNIFIED_TABLE_CONFIG.COLUMNS.map((column) => (
        <React.Fragment key={column.key}>
          {/* Total cantidad */}
          <TableCell
            style={{
              ...styles.cellRepairScreen,
              padding: TABLE_CONFIG.CELL_STYLES.padding,
              textAlign: "center",
              backgroundColor: cantidadTotals[column.key] > 0 
                ? getCellStyle(cantidadTotals[column.key], column.key).backgroundColor 
                : "#f5f5f5",
              borderRight: `1px solid rgba(0,0,0,0.08)`,
              borderBottom: `1px solid rgba(0,0,0,0.15)`,
            }}>
            <Text
              style={{
                color: cantidadTotals[column.key] > 0 
                  ? getCellStyle(cantidadTotals[column.key], column.key).color 
                  : "#000000",
                fontWeight: getCellStyle(cantidadTotals[column.key], column.key).fontWeight,
                fontSize: TABLE_CONFIG.FONT_SIZES.normal
              }}>
              {formatCantidad(cantidadTotals[column.key])}
            </Text>
          </TableCell>
          {/* Total aleación */}
          <TableCell
            style={{
              ...styles.cellRepairScreen,
              padding: TABLE_CONFIG.CELL_STYLES.padding,
              textAlign: "center",
              backgroundColor: aleacionTotals[column.key] > 0 
                ? getCellStyle(aleacionTotals[column.key], column.key).backgroundColor 
                : "#f5f5f5",
              borderRight: `1px solid rgba(0,0,0,0.08)`,
              borderBottom: `1px solid rgba(0,0,0,0.15)`,
            }}>
            <Text
              style={{
                color: aleacionTotals[column.key] > 0 
                  ? getCellStyle(aleacionTotals[column.key], column.key).color 
                  : "#000000",
                fontWeight: getCellStyle(aleacionTotals[column.key], column.key).fontWeight,
                fontSize: TABLE_CONFIG.FONT_SIZES.small
              }}>
              {formatValue(aleacionTotals[column.key])}
            </Text>
          </TableCell>
          {/* Total fino */}
          <TableCell
            style={{
              ...styles.cellRepairScreen,
              padding: TABLE_CONFIG.CELL_STYLES.padding,
              textAlign: "center",
              backgroundColor: finoTotals[column.key] > 0 
                ? getCellStyle(finoTotals[column.key], column.key).backgroundColor 
                : "#f5f5f5",
              borderRight: `1px solid rgba(0,0,0,0.08)`,
              borderBottom: `1px solid rgba(0,0,0,0.15)`,
            }}>
            <Text
              style={{
                color: finoTotals[column.key] > 0 
                  ? getCellStyle(finoTotals[column.key], column.key).color 
                  : "#000000",
                fontWeight: getCellStyle(finoTotals[column.key], column.key).fontWeight,
                fontSize: TABLE_CONFIG.FONT_SIZES.small
              }}>
              {formatValue(finoTotals[column.key])}
            </Text>
          </TableCell>
        </React.Fragment>
      ))}
      {/* Total general cantidad */}
      <TableCell
        style={{
          ...styles.cellRepairScreen,
          padding: TABLE_CONFIG.CELL_STYLES.padding,
          textAlign: "center",
          backgroundColor: cantidadTotals.total > 0 
            ? getCellStyle(cantidadTotals.total, "total").backgroundColor 
            : "#f5f5f5",
          borderRight: `1px solid rgba(0,0,0,0.08)`,
          borderBottom: `1px solid rgba(0,0,0,0.15)`,
        }}>
        <Text
          style={{
            color: cantidadTotals.total > 0 
              ? getCellStyle(cantidadTotals.total, "total").color 
              : "#000000",
            fontWeight: getCellStyle(cantidadTotals.total, "total").fontWeight,
            fontSize: TABLE_CONFIG.FONT_SIZES.normal
          }}>
          {formatCantidad(cantidadTotals.total)}
        </Text>
      </TableCell>
      {/* Total general aleación */}
      <TableCell
        style={{
          ...styles.cellRepairScreen,
          padding: TABLE_CONFIG.CELL_STYLES.padding,
          textAlign: "center",
          backgroundColor: aleacionTotals.total > 0 
            ? getCellStyle(aleacionTotals.total, "total").backgroundColor 
            : "#f5f5f5",
          borderRight: `1px solid rgba(0,0,0,0.08)`,
          borderBottom: `1px solid rgba(0,0,0,0.15)`,
        }}>
        <Text
          style={{
            color: aleacionTotals.total > 0 
              ? getCellStyle(aleacionTotals.total, "total").color 
              : "#000000",
            fontWeight: getCellStyle(aleacionTotals.total, "total").fontWeight,
            fontSize: TABLE_CONFIG.FONT_SIZES.normal
          }}>
          {formatValue(aleacionTotals.total)}
        </Text>
      </TableCell>
      {/* Total general fino */}
      <TableCell
        style={{
          ...styles.cellRepairScreen,
          padding: TABLE_CONFIG.CELL_STYLES.padding,
          textAlign: "center",
          backgroundColor: finoTotals.total > 0 
            ? getCellStyle(finoTotals.total, "total").backgroundColor 
            : "#f5f5f5",
          borderBottom: `1px solid rgba(0,0,0,0.15)`,
        }}>
        <Text
          style={{
            color: finoTotals.total > 0 
              ? getCellStyle(finoTotals.total, "total").color 
              : "#000000",
            fontWeight: getCellStyle(finoTotals.total, "total").fontWeight,
            fontSize: TABLE_CONFIG.FONT_SIZES.normal
          }}>
          {formatValue(finoTotals.total)}
        </Text>
      </TableCell>
    </TableRow>
  );

  return (
    <View style={styles.containerRepairScreen}>
      {isLoading ? (
        /* Animación de carga con skeletons */
        <View style={{
          margin: 8,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          width: "calc(100% - 16px)",
          padding: 16,
        }}>
          <Paper
            style={{
              boxShadow: "none",
              backgroundColor: "transparent",
            }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {/* Columna de días */}
                  <TableCell
                    style={{
                      backgroundColor: FUNDICION_STATUS_COLORS.STATUS_RETRASO,
                      padding: TABLE_CONFIG.CELL_STYLES.headerPadding,
                      textAlign: "center",
                    }}>
                    <Skeleton variant="text" width="60%" height={24} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
                  </TableCell>
                  {/* 7 columnas principales con 3 subcolumnas cada una */}
                  {[...Array(7)].map((_, i) => (
                    <TableCell
                      key={i}
                      colSpan={3}
                      style={{
                        backgroundColor: FUNDICION_STATUS_COLORS.STATUS_RETRASO,
                        padding: TABLE_CONFIG.CELL_STYLES.headerPadding,
                        textAlign: "center",
                      }}>
                      <Skeleton variant="text" width="70%" height={24} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
                    </TableCell>
                  ))}
                  {/* Columna de total */}
                  <TableCell
                    colSpan={3}
                    style={{
                      backgroundColor: FUNDICION_STATUS_COLORS.STATUS_RETRASO,
                      padding: TABLE_CONFIG.CELL_STYLES.headerPadding,
                      textAlign: "center",
                    }}>
                    <Skeleton variant="text" width="60%" height={24} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
                  </TableCell>
                </TableRow>
                {/* Segunda fila de headers */}
                <TableRow>
                  {/* Celda del botón */}
                  <TableCell
                    style={{
                      backgroundColor: FUNDICION_STATUS_COLORS.STATUS_RETRASO,
                      padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
                      textAlign: "center",
                    }}>
                    <Skeleton variant="rectangular" width={60} height={24} sx={{ bgcolor: "rgba(255,255,255,0.3)" }} />
                  </TableCell>
                  {/* Subcolumnas (cantidad, aleación, fino) x 7 columnas principales + total */}
                  {[...Array(8)].map((_, i) => (
                    <React.Fragment key={i}>
                      <TableCell
                        style={{
                          backgroundColor: "#f5f5f5",
                          padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
                          textAlign: "center",
                        }}>
                        <Skeleton variant="text" width="80%" height={20} />
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: "#f5f5f5",
                          padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
                          textAlign: "center",
                        }}>
                        <Skeleton variant="text" width="80%" height={20} />
                      </TableCell>
                      <TableCell
                        style={{
                          backgroundColor: "#f5f5f5",
                          padding: TABLE_CONFIG.CELL_STYLES.subHeaderPadding,
                          textAlign: "center",
                        }}>
                        <Skeleton variant="text" width="80%" height={20} />
                      </TableCell>
                    </React.Fragment>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(SKELETON_CONFIG.rows)].map((_, rowIndex) => (
                  <TableRow
                    key={rowIndex}
                    style={{
                      backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f5f5f5",
                    }}>
                    {/* Columna de aleación */}
                    <TableCell
                      style={{
                        padding: TABLE_CONFIG.CELL_STYLES.padding,
                        backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f5f5f5",
                      }}>
                      <Skeleton variant="text" width={SKELETON_CONFIG.widthLabel} height={SKELETON_CONFIG.height} />
                    </TableCell>
                    {/* 7 columnas principales x 3 subcolumnas = 21 columnas */}
                    {[...Array(21)].map((_, colIndex) => (
                      <TableCell
                        key={colIndex}
                        style={{
                          padding: TABLE_CONFIG.CELL_STYLES.padding,
                          textAlign: "center",
                          backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f5f5f5",
                        }}>
                        <Skeleton variant="text" width={SKELETON_CONFIG.widthValue} height={SKELETON_CONFIG.height} />
                      </TableCell>
                    ))}
                    {/* 3 columnas de total */}
                    {[...Array(3)].map((_, colIndex) => (
                      <TableCell
                        key={colIndex}
                        style={{
                          padding: TABLE_CONFIG.CELL_STYLES.padding,
                          textAlign: "center",
                          backgroundColor: rowIndex % 2 === 0 ? "#ffffff" : "#f5f5f5",
                        }}>
                        <Skeleton variant="text" width={SKELETON_CONFIG.widthValue} height={SKELETON_CONFIG.height} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </View>
      ) : (
        /* Tabla unificada */
        <View style={{
          margin: 8,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          width: "calc(100% - 16px)"
        }}>
          <TableContainer
            component={Paper}
            style={{
              ...styles.tableContainerRepairScreen,
              boxShadow: "none",
              borderRadius: 0,
              maxWidth: "100%",
              overflowX: "auto",
              width: "100%"
            }}>
            <Table stickyHeader size="small" sx={{ minWidth: 600, tableLayout: "auto" }}>
              <UnifiedTableHeader 
                lang={lang} 
                onExport={exportToExcel}
                isLoading={isLoading}
                hasData={datosFundicion.length > 0}
              />
              <TableBody>
                {datosFundicion.map((cantidadItem, index) => {
                  const aleacionItem = datosOroCordoba[index];
                  const finoItem = datosOroCordobaFino[index];
                  return (
                    <UnifiedDataRow 
                      key={index} 
                      cantidadItem={cantidadItem}
                      aleacionItem={aleacionItem} 
                      finoItem={finoItem} 
                      formatValue={formatGramos}
                      index={index}
                    />
                  );
                })}
                <UnifiedTotalRow 
                  cantidadTotals={totalsumaValores}
                  aleacionTotals={totalOroCordoba} 
                  finoTotals={totalOroCordobaFino} 
                  formatValue={formatGramos} 
                />
              </TableBody>
            </Table>
          </TableContainer>
        </View>
      )}
    </View>
  );
};

export default FundicionStatus;
